import React, { useState, useEffect } from 'react';
import { Plus, ChevronDown, Search, Clock, Edit, Trash2, Shield } from 'lucide-react';
import CreateServiceModal from '../components/CreateServiceModal';
import ServiceDetailsModal from '../components/ServiceDetailsModal';
import CategoryModal from '../components/CategoryModal';
import { getServices, getServiceCategories, Service, ServiceCategory, deleteServiceCategory, deleteService } from '../lib/services';
import { useBusiness } from '../contexts/BusinessContext';
import PermissionGuard from '../components/PermissionGuard';


export default function Services() {
  const { selectedBusiness } = useBusiness();
  const [activeTab, setActiveTab] = useState<'services' | 'memberships'>('services');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedPriceOptions, setExpandedPriceOptions] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateServiceModalOpen, setIsCreateServiceModalOpen] = useState(false);
  const [isServiceDetailsModalOpen, setIsServiceDetailsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [categoryModalMode, setCategoryModalMode] = useState<'create' | 'edit'>('create');
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const [servicesData, categoriesData] = await Promise.all([
        getServices(selectedBusiness.id),
        getServiceCategories(selectedBusiness.id)
      ]);
      
      console.log('Loaded data:', {
        services: servicesData.length,
        categories: categoriesData.length
      });
      
      setServices(servicesData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedBusiness?.id]);

  // Memberships are retired — recurring bookings (booking_series) replaced
  // them. The tab never renders and formerly-membership services show in the
  // regular Services list.
  const hasMemberships = false;

  // Filter categories and services based on active tab
  const filteredByTabCategories = React.useMemo(() => 
    categories.filter(cat => 
      activeTab === 'memberships' 
        ? cat.is_membership === true  // Only show if explicitly true
        : true  // Memberships retired — everything shows in Services
    ),
    [categories, activeTab]
  );

  const filteredByTabServices = React.useMemo(() =>
    services.filter(svc => 
      activeTab === 'memberships' 
        ? svc.is_membership === true  // Only show if explicitly true
        : true  // Memberships retired — everything shows in Services
    ),
    [services, activeTab]
  );

  // Memoize grouped services by category
  const servicesByCategory = React.useMemo(() => filteredByTabCategories.map(category => ({
    ...category,
    services: filteredByTabServices.filter(service => service.category_id === category.id)
  })), [filteredByTabCategories, filteredByTabServices]);

  // Memoize filtered categories
  const filteredCategories = React.useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return servicesByCategory.filter(category => {
      // Check if category name matches
      if (category.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Check if any service in the category matches
      return category.services.some(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      );
    }).map(category => ({ 
      ...category,
      // Filter services within the category
      services: category.services.filter(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      )
    }));
  }, [searchQuery, servicesByCategory]);

  // Auto-expand categories with matching items when searching
  useEffect(() => {
    if (searchQuery) {
      setExpandedCategories(new Set(filteredCategories.map(c => c.id)));
    } else {
      setExpandedCategories(new Set());
    }
  }, [searchQuery, filteredCategories]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const togglePriceOptions = (serviceId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setExpandedPriceOptions(prev => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };


  const handleDeleteCategory = async (e: React.MouseEvent, categoryId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all services in this category.')) {
      return;
    }

    try {
      await deleteServiceCategory(categoryId);
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category. Please try again.');
    }
  };


  const handleServiceCreated = async () => {
    await loadData();
    setIsCreateServiceModalOpen(false);
  };

  // Drag and drop functionality disabled for now
  // const handleDragStart = (e: React.DragEvent, type: 'category' | 'service', id: string) => {
  //   e.stopPropagation();
  // };

  // const handleDragOver = (e: React.DragEvent) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // };

  // const handleDrop = async (e: React.DragEvent, targetId: string, type: 'category' | 'service') => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   // Drag and drop functionality disabled
  // };

  const handleDeleteService = async (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await deleteService(serviceId);
      await loadData();
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('Failed to delete service. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
        <div className="bg-red-50 border border-red-200 rounded-[12px] p-6 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center"
             style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>Error Loading Services</h2>
          <p className="text-[15px] text-red-600 font-medium" style={{ letterSpacing: '-0.01em' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="services.pageAccess">
      <div 
        className="min-h-screen bg-gray-50" 
        data-services-page
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
        }}
      >
        <div className="w-full px-4 py-4 sm:px-6 sm:py-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="space-y-3 sm:space-y-4">
              {/* Title and Search Row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <h1 
                  className="text-2xl font-bold text-gray-900"
                  style={{ letterSpacing: '-0.01em' }}
                >
                  Services
                </h1>
                <div className="relative flex-1 sm:flex-initial sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-[15px] bg-white border border-gray-200/60 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                    style={{ 
                      minHeight: '44px',
                      letterSpacing: '-0.01em',
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation'
                    }}
                  />
                </div>
              </div>

              {/* Tab Navigation - memberships are retired, so this stays hidden */}
              {hasMemberships && (
                <div className="flex gap-2 border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('services')}
                    className={`px-4 py-3 text-[15px] font-semibold transition-all duration-150 border-b-2 touch-manipulation ${
                      activeTab === 'services'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 active:text-gray-900'
                    }`}
                    style={{ 
                      minHeight: '44px',
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Services
                  </button>
                  {hasMemberships && (
                    <button
                      onClick={() => setActiveTab('memberships')}
                      className={`px-4 py-3 text-[15px] font-semibold transition-all duration-150 border-b-2 touch-manipulation ${
                        activeTab === 'memberships'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 active:text-gray-900'
                      }`}
                      style={{ 
                        minHeight: '44px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      Memberships
                    </button>
                  )}
                </div>
              )}
              
              {/* Action Buttons Row */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                  {activeTab === 'services' ? (
                    <>
                      <button
                        onClick={() => {
                          setSelectedCategory(null);
                          setCategoryModalMode('create');
                          setIsCategoryModalOpen(true);
                        }}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 text-[15px] font-semibold text-gray-600 active:text-gray-900 active:bg-gray-100 border border-gray-200/60 rounded-[10px] transition-all duration-150 shadow-sm touch-manipulation"
                        style={{ 
                          minHeight: '44px',
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Category</span>
                      </button>
                      <button 
                        onClick={() => setIsCreateServiceModalOpen(true)}
                        className="flex items-center justify-center gap-2 bg-blue-600 active:bg-blue-700 text-white px-4 py-2.5 rounded-[10px] font-semibold transition-all duration-150 shadow-sm touch-manipulation"
                        style={{ 
                          minHeight: '44px',
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em',
                          fontSize: '15px'
                        }}
                      >
                        <Plus className="h-4 w-4" />
                        <span>Create Service</span>
                      </button>
                    </>
                  ) : (
                    <button 
                      onClick={() => setIsCreateServiceModalOpen(true)}
                      className="flex items-center justify-center gap-2 bg-blue-600 active:bg-blue-700 text-white px-4 py-2.5 rounded-[10px] font-semibold transition-all duration-150 shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em',
                        fontSize: '15px'
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      <span>Create Membership</span>
                    </button>
                  )}
                </div>
            </div>

            {/* Main Content Container */}
            <div className="space-y-3">
              {/* Services Tab */}
              {activeTab === 'services' && (
                <>
                  {loading ? (
                    <div className="flex items-center justify-center h-64 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="text-center">
                        <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Loading services...</p>
                      </div>
                    </div>
                  ) : filteredCategories.length === 0 ? (
                <div className="flex items-center justify-center h-64 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-[12px] flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>No services found</h3>
                    <p className="text-[15px] text-gray-500 mb-4" style={{ letterSpacing: '-0.01em' }}>
                      {searchQuery ? 'Try adjusting your search terms' : 'Start by creating your first service'}
                    </p>
                    <button
                      onClick={() => setIsCreateServiceModalOpen(true)}
                      className="bg-blue-600 active:bg-blue-700 text-white px-4 py-2.5 rounded-[10px] font-semibold transition-all duration-150 shadow-sm touch-manipulation"
                      style={{ 
                        minHeight: '44px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em',
                        fontSize: '15px'
                      }}
                    >
                      Create Service
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCategories.map((category) => (
                    <div key={category.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div className="p-4 sm:p-6">
                        {/* Category Header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <button
                              onClick={() => toggleCategory(category.id)}
                              className="flex items-center gap-2 text-[16px] font-semibold text-gray-900 active:text-blue-600 transition-colors touch-manipulation"
                              style={{ 
                                WebkitTapHighlightColor: 'transparent',
                                letterSpacing: '-0.01em',
                                minHeight: '44px'
                              }}
                            >
                              <ChevronDown 
                                className={`h-5 w-5 transition-transform duration-200 flex-shrink-0 ${
                                  expandedCategories.has(category.id) ? 'rotate-180' : ''
                                }`} 
                              />
                              <span className="truncate">{category.name}</span>
                            </button>
                            <span className="bg-gray-100 text-gray-600 text-[13px] font-semibold px-2.5 py-0.5 rounded-[6px] flex-shrink-0"
                                  style={{ letterSpacing: '-0.01em' }}>
                              {category.services.length}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                setSelectedCategory(category);
                                setCategoryModalMode('edit');
                                setIsCategoryModalOpen(true);
                              }}
                              className="p-2 text-gray-400 active:text-blue-600 active:bg-blue-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                              style={{ 
                                minHeight: '36px',
                                minWidth: '36px',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                              title="Edit category"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                handleDeleteCategory(e, category.id);
                              }}
                              className="p-2 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                              style={{ 
                                minHeight: '36px',
                                minWidth: '36px',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                              title="Delete category"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Services List */}
                        {expandedCategories.has(category.id) && (
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {category.services.map((service) => (
                              <div
                                key={service.id}
                                className="bg-gray-50/50 border border-gray-200/60 rounded-[12px] p-3 sm:p-4 active:bg-gray-100 transition-all duration-150 cursor-pointer touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                                onClick={() => {
                                  setSelectedService(service);
                                  setIsServiceDetailsModalOpen(true);
                                }}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="text-[15px] font-semibold text-gray-900 flex-1" style={{ letterSpacing: '-0.01em' }}>
                                    {service.name}
                                  </h4>
                                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[11px] font-semibold bg-green-100 text-green-800"
                                          style={{ letterSpacing: '-0.01em' }}>
                                      Active
                                    </span>
                                  </div>
                                </div>
                                
                                {service.description && (
                                  <p className="text-[13px] text-gray-600 mb-3 line-clamp-2" style={{ letterSpacing: '-0.01em' }}>
                                    {service.description}
                                  </p>
                                )}
                                
                                <div className="space-y-3">
                                  {/* Primary Price/Duration Display */}
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4 text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                      {service.price_options && service.price_options.length > 0 && (() => {
                                        const po = service.price_options![0];
                                        const pt = (po as any).pricing_type;
                                        const isPerUnit = pt === 'per_unit' || pt === 'per_sqft';
                                        const label = pt === 'per_sqft' ? '/sqft' : pt === 'per_unit' ? '/unit' : '';
                                        return (
                                          <>
                                            {!isPerUnit && (
                                              <div className="flex items-center gap-1">
                                                <Clock className="h-4 w-4" />
                                                {po.duration_minutes} min
                                              </div>
                                            )}
                                            <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                              {(() => {
                                                const base = isPerUnit ? ((po as any).unit_price || 0) : po.price;
                                                const compareAt = (po as any).compare_at_price;
                                                const showCompare = compareAt != null && compareAt > base;
                                                return (
                                                  <>
                                                    {showCompare && (
                                                      <span className="text-gray-400 font-normal line-through mr-1">{`$${Number(compareAt).toLocaleString()}${label}`}</span>
                                                    )}
                                                    {`$${base}${label}`}
                                                  </>
                                                );
                                              })()}
                                            </div>
                                            {isPerUnit && (
                                              <span className="text-[11px] text-gray-400">{(po as any).duration_per_unit || 0} hrs{label}</span>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedService(service);
                                          setIsServiceDetailsModalOpen(true);
                                        }}
                                        className="p-2 text-gray-400 active:text-blue-600 active:bg-blue-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                                        style={{ 
                                          minHeight: '36px',
                                          minWidth: '36px',
                                          WebkitTapHighlightColor: 'transparent'
                                        }}
                                        title="Edit service"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          handleDeleteService(e, service.id);
                                        }}
                                        className="p-2 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                                        style={{ 
                                          minHeight: '36px',
                                          minWidth: '36px',
                                          WebkitTapHighlightColor: 'transparent'
                                        }}
                                        title="Delete service"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* All Price Options */}
                                  {service.price_options && service.price_options.length > 1 && (
                                    <div className="border-t border-gray-200/60 pt-3">
                                      <button
                                        onClick={(e) => togglePriceOptions(service.id, e)}
                                        className="w-full flex items-center justify-between text-[13px] font-semibold text-gray-600 active:text-gray-900 transition-colors touch-manipulation"
                                        style={{ 
                                          WebkitTapHighlightColor: 'transparent',
                                          letterSpacing: '-0.01em',
                                          minHeight: '36px'
                                        }}
                                      >
                                        <span>
                                          Price Options ({service.price_options.length})
                                        </span>
                                        <ChevronDown 
                                          className={`h-4 w-4 transition-transform duration-200 ${
                                            expandedPriceOptions.has(service.id) ? 'rotate-180' : ''
                                          }`} 
                                        />
                                      </button>
                                      {expandedPriceOptions.has(service.id) && (
                                        <div className="mt-3 space-y-2">
                                          {service.price_options.map((option, index) => {
                                            const pt = (option as any).pricing_type;
                                            const isPerUnit = pt === 'per_unit' || pt === 'per_sqft';
                                            const label = pt === 'per_sqft' ? '/sqft' : pt === 'per_unit' ? '/unit' : '';
                                            return (
                                              <div key={index} className="flex items-center justify-between text-[13px]">
                                                <div className="flex items-center gap-2 truncate pr-2">
                                                  <span className="text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>
                                                    {option.name || `Option ${index + 1}`}
                                                  </span>
                                                  {isPerUnit && (
                                                    <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                      {pt === 'per_sqft' ? 'SQ FT' : 'UNIT'}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                  {isPerUnit ? (
                                                    <>
                                                      <span className="text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                                        {(option as any).duration_per_unit || 0} hrs{label}
                                                      </span>
                                                      <span className="text-[13px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                                        {(() => {
                                                          const base = (option as any).unit_price || 0;
                                                          const compareAt = (option as any).compare_at_price;
                                                          const showCompare = compareAt != null && compareAt > base;
                                                          return (
                                                            <>
                                                              {showCompare && (
                                                                <span className="text-gray-400 font-normal line-through mr-1">${Number(compareAt).toLocaleString()}{label}</span>
                                                              )}
                                                              ${base}{label}
                                                            </>
                                                          );
                                                        })()}
                                                      </span>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <span className="text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                                        {option.duration_minutes} min
                                                      </span>
                                                      <span className="text-[13px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                                        {(() => {
                                                          const base = option.price;
                                                          const compareAt = (option as any).compare_at_price;
                                                          const showCompare = compareAt != null && compareAt > base;
                                                          return (
                                                            <>
                                                              {showCompare && (
                                                                <span className="text-gray-400 font-normal line-through mr-1">${Number(compareAt).toLocaleString()}</span>
                                                              )}
                                                              ${base}
                                                            </>
                                                          );
                                                        })()}
                                                      </span>
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </>
              )}

              {/* Memberships Tab */}
              {activeTab === 'memberships' && (
                <>
                  {loading ? (
                    <div className="flex items-center justify-center h-64 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="text-center">
                        <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Loading memberships...</p>
                      </div>
                    </div>
                  ) : filteredCategories.length === 0 ? (
                    <div className="flex items-center justify-center h-64 bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="text-center">
                        <div className="w-12 h-12 bg-blue-50 rounded-[12px] flex items-center justify-center mx-auto mb-4">
                          <Shield className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>No memberships found</h3>
                        <p className="text-[15px] text-gray-500 mb-4" style={{ letterSpacing: '-0.01em' }}>
                          {searchQuery ? 'Try adjusting your search terms' : 'Create recurring services that automatically book appointments for your customers'}
                        </p>
                        <button
                          onClick={() => setIsCreateServiceModalOpen(true)}
                          className="bg-blue-600 active:bg-blue-700 text-white px-4 py-2.5 rounded-[10px] font-semibold transition-all duration-150 shadow-sm touch-manipulation"
                          style={{ 
                            minHeight: '44px',
                            WebkitTapHighlightColor: 'transparent',
                            letterSpacing: '-0.01em',
                            fontSize: '15px'
                          }}
                        >
                          Create Membership
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredCategories.map((category) => (
                        <div key={category.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                          <div className="p-4 sm:p-6">
                            {/* Category Header */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <button
                                  onClick={() => toggleCategory(category.id)}
                                  className="flex items-center gap-2 text-[16px] font-semibold text-gray-900 active:text-blue-600 transition-colors touch-manipulation"
                                  style={{ 
                                    WebkitTapHighlightColor: 'transparent',
                                    letterSpacing: '-0.01em',
                                    minHeight: '44px'
                                  }}
                                >
                                  <ChevronDown 
                                    className={`h-5 w-5 transition-transform duration-200 flex-shrink-0 ${
                                      expandedCategories.has(category.id) ? 'rotate-180' : ''
                                    }`} 
                                  />
                                  <span className="truncate">{category.name}</span>
                                </button>
                                <span className="bg-gray-100 text-gray-600 text-[13px] font-semibold px-2.5 py-0.5 rounded-[6px] flex-shrink-0"
                                      style={{ letterSpacing: '-0.01em' }}>
                                  {category.services.length}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => {
                                    setSelectedCategory(category);
                                    setCategoryModalMode('edit');
                                    setIsCategoryModalOpen(true);
                                  }}
                                  className="p-2 text-gray-400 active:text-blue-600 active:bg-blue-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                                  style={{ 
                                    minHeight: '36px',
                                    minWidth: '36px',
                                    WebkitTapHighlightColor: 'transparent'
                                  }}
                                  title="Edit category"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    handleDeleteCategory(e, category.id);
                                  }}
                                  className="p-2 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                                  style={{ 
                                    minHeight: '36px',
                                    minWidth: '36px',
                                    WebkitTapHighlightColor: 'transparent'
                                  }}
                                  title="Delete category"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                            {/* Services List */}
                            {expandedCategories.has(category.id) && (
                              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {category.services.map((service) => (
                                  <div
                                    key={service.id}
                                    className="bg-gray-50/50 border border-gray-200/60 rounded-[12px] p-3 sm:p-4 active:bg-gray-100 transition-all duration-150 cursor-pointer touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                                    onClick={() => {
                                      setSelectedService(service);
                                      setIsServiceDetailsModalOpen(true);
                                    }}
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <h4 className="text-[15px] font-semibold text-gray-900 flex-1" style={{ letterSpacing: '-0.01em' }}>
                                        {service.name}
                                      </h4>
                                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[11px] font-semibold bg-green-100 text-green-800"
                                              style={{ letterSpacing: '-0.01em' }}>
                                          Active
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {service.description && (
                                      <p className="text-[13px] text-gray-600 mb-3 line-clamp-2" style={{ letterSpacing: '-0.01em' }}>
                                        {service.description}
                                      </p>
                                    )}
                                    
                                    <div className="space-y-3">
                                      {/* Primary Price/Duration Display */}
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4 text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                          {service.price_options && service.price_options.length > 0 && (() => {
                                            const po = service.price_options![0];
                                            const pt = (po as any).pricing_type;
                                            const isPerUnit = pt === 'per_unit' || pt === 'per_sqft';
                                            const label = pt === 'per_sqft' ? '/sqft' : pt === 'per_unit' ? '/unit' : '';
                                            return (
                                              <>
                                                {!isPerUnit && (
                                                  <div className="flex items-center gap-1">
                                                    <Clock className="h-4 w-4" />
                                                    {po.duration_minutes} min
                                                  </div>
                                                )}
                                                <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                                  {(() => {
                                                    const base = isPerUnit ? ((po as any).unit_price || 0) : po.price;
                                                    const compareAt = (po as any).compare_at_price;
                                                    const showCompare = compareAt != null && compareAt > base;
                                                    return (
                                                      <>
                                                        {showCompare && (
                                                          <span className="text-gray-400 font-normal line-through mr-1">{`$${Number(compareAt).toLocaleString()}${label}`}</span>
                                                        )}
                                                        {`$${base}${label}`}
                                                      </>
                                                    );
                                                  })()}
                                                </div>
                                                {isPerUnit && (
                                                  <span className="text-[11px] text-gray-400">{(po as any).duration_per_unit || 0} hrs{label}</span>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedService(service);
                                              setIsServiceDetailsModalOpen(true);
                                            }}
                                            className="p-2 text-gray-400 active:text-blue-600 active:bg-blue-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                                            style={{ 
                                              minHeight: '36px',
                                              minWidth: '36px',
                                              WebkitTapHighlightColor: 'transparent'
                                            }}
                                            title="Edit service"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              handleDeleteService(e, service.id);
                                            }}
                                            className="p-2 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                                            style={{ 
                                              minHeight: '36px',
                                              minWidth: '36px',
                                              WebkitTapHighlightColor: 'transparent'
                                            }}
                                            title="Delete service"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </button>
                                        </div>
                                      </div>

                                      {/* All Price Options */}
                                      {service.price_options && service.price_options.length > 1 && (
                                        <div className="border-t border-gray-200/60 pt-3">
                                          <button
                                            onClick={(e) => togglePriceOptions(service.id, e)}
                                            className="w-full flex items-center justify-between text-[13px] font-semibold text-gray-600 active:text-gray-900 transition-colors touch-manipulation"
                                            style={{ 
                                              WebkitTapHighlightColor: 'transparent',
                                              letterSpacing: '-0.01em',
                                              minHeight: '36px'
                                            }}
                                          >
                                            <span>
                                              Price Options ({service.price_options.length})
                                            </span>
                                            <ChevronDown 
                                              className={`h-4 w-4 transition-transform duration-200 ${
                                                expandedPriceOptions.has(service.id) ? 'rotate-180' : ''
                                              }`} 
                                            />
                                          </button>
                                          {expandedPriceOptions.has(service.id) && (
                                            <div className="mt-3 space-y-2">
                                              {service.price_options.map((option, index) => {
                                                const pt = (option as any).pricing_type;
                                                const isPerUnit = pt === 'per_unit' || pt === 'per_sqft';
                                                const label = pt === 'per_sqft' ? '/sqft' : pt === 'per_unit' ? '/unit' : '';
                                                return (
                                                  <div key={index} className="flex items-center justify-between text-[13px]">
                                                    <div className="flex items-center gap-2 truncate pr-2">
                                                      <span className="text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>
                                                        {option.name || `Option ${index + 1}`}
                                                      </span>
                                                      {isPerUnit && (
                                                        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                                          {pt === 'per_sqft' ? 'SQ FT' : 'UNIT'}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                      {isPerUnit ? (
                                                        <>
                                                          <span className="text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                                            {(option as any).duration_per_unit || 0} hrs{label}
                                                          </span>
                                                          <span className="text-[13px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                                            {(() => {
                                                              const base = (option as any).unit_price || 0;
                                                              const compareAt = (option as any).compare_at_price;
                                                              const showCompare = compareAt != null && compareAt > base;
                                                              return (
                                                                <>
                                                                  {showCompare && (
                                                                    <span className="text-gray-400 font-normal line-through mr-1">${Number(compareAt).toLocaleString()}{label}</span>
                                                                  )}
                                                                  ${base}{label}
                                                                </>
                                                              );
                                                            })()}
                                                          </span>
                                                        </>
                                                      ) : (
                                                        <>
                                                          <span className="text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                                                            {option.duration_minutes} min
                                                          </span>
                                                          <span className="text-[13px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                                                            {(() => {
                                                              const base = option.price;
                                                              const compareAt = (option as any).compare_at_price;
                                                              const showCompare = compareAt != null && compareAt > base;
                                                              return (
                                                                <>
                                                                  {showCompare && (
                                                                    <span className="text-gray-400 font-normal line-through mr-1">${Number(compareAt).toLocaleString()}</span>
                                                                  )}
                                                                  ${base}
                                                                </>
                                                              );
                                                            })()}
                                                          </span>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>

        <CreateServiceModal 
          isOpen={isCreateServiceModalOpen}
          onClose={() => setIsCreateServiceModalOpen(false)}
          onSuccess={handleServiceCreated}
          businessId={selectedBusiness?.id}
          categories={filteredByTabCategories}
          onCategoryCreated={loadData}
          isMembership={activeTab === 'memberships'}
          modalTitle={activeTab === 'memberships' ? 'Create Membership' : 'Create Service'}
        />

        <ServiceDetailsModal
          isOpen={isServiceDetailsModalOpen}
          onClose={() => {
            setIsServiceDetailsModalOpen(false);
            setSelectedService(null);
          }}
          service={selectedService}
          onUpdate={loadData}
        />

        <CategoryModal
          isOpen={isCategoryModalOpen}
          onClose={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
          }}
          onSuccess={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
            loadData();
          }}
          businessId={selectedBusiness?.id}
          category={selectedCategory}
          mode={categoryModalMode}
          isMembership={activeTab === 'memberships'}
        />

      </div>
    </PermissionGuard>
  );
}